import { T } from "@freightsimple/ui-core/Typography/T";
import PageTitle from "@/Shared/Layout/PageTitle";
import type { UUID } from "@/Shared/types";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import type { SharedDedicated } from "@freightsimple/generated-dashboard-openapi-client/models/SharedDedicated";
import { BookShipmentDedicatedLogisticsOffer } from "./BookShipmentDedicatedLogisticsOffer";
import { BookShipmentDedicatedLogisticsOfferButtonPositionType } from "../Types/BookShipmentDedicatedLogisticsOfferButtonPositionType";
import { BookShipmentTwoColumnView } from "./BookShipmentTwoColumnView";

interface NoQuotesProps {
  onModify: () => void;
  onRequestManualQuotes: (
    shipmentId: UUID,
    equipmentType: EquipmentType,
    sharedDedicated: SharedDedicated,
    tarpRequired: boolean | undefined,
    linearFeet: number | undefined,
    notes: string,
  ) => Promise<void>;
  shipment: PreBookingShipment;
}

export function BookShipmentNoQuotes(props: NoQuotesProps) {
  const { onModify, shipment, onRequestManualQuotes } = props;

  if (shipment.manualQuotingOpen) {
    return (
      <>
        <PageTitle>Waiting for manual quotes</PageTitle>

        <div className="flex flex-row items-start" style={{ padding: "64px" }}>
          <div className="flex flex-col items-start">
            <h2 className="text-2xl font-bold text-foreground">
              🦸‍♀️ Searching for the best quotes
            </h2>
            <div className="h-[24px]" />
            <div className="flex flex-col items-start">
              <T size="base" color="secondary">
                Our logistics team is looking for manual quotes for your
                shipment
              </T>
              <div className="h-[8px]" />
              <T size="base" color="secondary">
                When quotes are ready you will be able to find them here
              </T>
              <div className="h-[24px]" />
              {shipment.manualQuotingNotes !== "" && (
                <>
                  <T size="base" color="secondary">
                    You provided these instructions to our team:
                  </T>
                  <div style={{ fontStyle: "italic" }}>
                    {shipment.manualQuotingNotes}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="w-[64px] shrink-0" />
          <BookShipmentTwoColumnView
            pickupDeadline={props.shipment.pickupDeadline}
            lineItems={props.shipment.lineItems}
            pickupDate={props.shipment.pickupDate}
            pickupLocation={props.shipment.pickupLocation}
            deliveryLocation={props.shipment.deliveryLocation}
            deliveryDeadline={props.shipment.deliveryDeadline}
            equipmentType={props.shipment.equipmentType}
            exclusiveUse={props.shipment.exclusiveUseNeeded}
            tarpRequired={props.shipment.tarpRequired}
            linearFeet={props.shipment.linearFeet}
            pickupReferenceNumber={props.shipment.pickupReferenceNumber}
            deliveryReferenceNumber={props.shipment.deliveryReferenceNumber}
            deliveryAmazonReferenceId={props.shipment.deliveryAmazonReferenceId}
            deliveryAmazonShipmentId={props.shipment.deliveryAmazonShipmentId}
          />
        </div>
      </>
    );
  } else {
    return (
      <>
        <PageTitle>No quotes</PageTitle>
        <div className="flex flex-col items-center">
          <div
            style={{
              color: "var(--color-content-muted)",
              fontSize: "15px",
              marginBottom: "4px",
            }}
          >
            Sorry, we couldn&apos;t find any instant quotes for your shipment
          </div>
          <h2
            className="text-2xl font-bold text-foreground"
            style={{ fontStyle: "italic" }}
          >
            ...but our dedicated logistics team can help
          </h2>
          <BookShipmentDedicatedLogisticsOffer
            onModify={onModify}
            pickupDeadline={props.shipment.pickupDeadline}
            onRequestManualQuotes={onRequestManualQuotes}
            shipmentId={props.shipment.shipmentId}
            lineItems={props.shipment.lineItems}
            pickupDate={props.shipment.pickupDate}
            pickupLocation={props.shipment.pickupLocation}
            deliveryLocation={props.shipment.deliveryLocation}
            deliveryDeadline={props.shipment.deliveryDeadline}
            buttonPosition={
              BookShipmentDedicatedLogisticsOfferButtonPositionType.Top
            }
            equipmentType={props.shipment.equipmentType}
            exclusiveUse={props.shipment.exclusiveUseNeeded}
            tarpRequired={props.shipment.tarpRequired}
            linearFeet={props.shipment.linearFeet}
            pickupReferenceNumber={props.shipment.pickupReferenceNumber}
            deliveryReferenceNumber={props.shipment.deliveryReferenceNumber}
            deliveryAmazonReferenceId={props.shipment.deliveryAmazonReferenceId}
            deliveryAmazonShipmentId={props.shipment.deliveryAmazonShipmentId}
          />
        </div>
      </>
    );
  }
}
